import React, { useState, useEffect } from 'react';
import { Bell, Send, CheckCircle2, X, Loader2, AlertCircle, User, Clock, Calendar, ClipboardCheck, LogIn, LogOut, Trash2, CheckCheck } from 'lucide-react';
import api from '../api';

// Notification type icon + color mapper
const getNotifMeta = (type = '') => {
  const t = type.toLowerCase();
  if (t.includes('leave'))     return { icon: Calendar,       color: 'text-purple-600', bg: 'bg-purple-50',  border: 'border-purple-100', label: 'Leave Request' };
  if (t.includes('clockin') || t.includes('clock_in') || t.includes('punch_in'))
                                return { icon: LogIn,          color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', label: 'Clock In' };
  if (t.includes('clockout') || t.includes('clock_out') || t.includes('punch_out'))
                                return { icon: LogOut,         color: 'text-orange-600', bg: 'bg-orange-50',  border: 'border-orange-100',  label: 'Clock Out' };
  if (t.includes('task'))      return { icon: ClipboardCheck, color: 'text-blue-600',   bg: 'bg-blue-50',    border: 'border-blue-100',    label: 'Task' };
  if (t.includes('general'))   return { icon: Bell,           color: 'text-slate-600',  bg: 'bg-slate-50',   border: 'border-slate-100',   label: 'General' };
  return                         { icon: Bell,                 color: 'text-slate-600',  bg: 'bg-slate-50',   border: 'border-slate-100',   label: 'Notification' };
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export default function NotificationsPage({ setActiveTab }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [currentTab, setCurrentTab] = useState('inbox'); // 'inbox' | 'send'

  // Send form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // 'success' | 'error'
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    fetchNotifications();
    // Auto-refresh every 30s
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/admin/notifications');
      const data = Array.isArray(res.data) ? res.data : (res.data || []);
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post('/admin/notifications/read', {});
      setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch (err) {
      console.error('Mark read failed:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    setSendStatus(null);
    setSendError('');
    try {
      await api.post('/admin/notifications/send-general', { title: title.trim(), message: message.trim() });
      setSendStatus('success');
      setTitle('');
      setMessage('');
      setTimeout(() => setSendStatus(null), 4000);
    } catch (err) {
      setSendStatus('error');
      setSendError(err.response?.data?.message || 'Failed to send. Try again.');
    } finally {
      setSending(false);
    }
  };

  const handleNotifClick = (notif) => {
    if (!setActiveTab) return;
    const data = notif.data || {};
    const t = (notif.type || data.type || '').toLowerCase();
    const txt = (data.title || data.message || '').toLowerCase();
    const combined = t + ' ' + txt;

    if (combined.includes('leave')) setActiveTab('Leave Requests');
    else if (combined.includes('task')) setActiveTab('Task Manager');
    else if (combined.includes('report')) setActiveTab('Daily Reports');
    else if (combined.includes('clock') || combined.includes('punch') || combined.includes('attendance')) setActiveTab('Attendance');
    else if (combined.includes('meeting')) setActiveTab('Meetings');
  };

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <div className="space-y-5 animate-in fade-in duration-300">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
            <Bell size={18} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-[17px] font-black text-slate-800 leading-tight">Notifications Center</h1>
            <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
        </div>
        {/* Tab Toggle */}
        <div className="flex bg-slate-100 p-1 rounded-lg gap-1">
          <button
            onClick={() => setCurrentTab('inbox')}
            className={`px-4 py-1.5 rounded-md text-[12px] font-black transition-all flex items-center gap-2 ${currentTab === 'inbox' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Bell size={13} /> Inbox
            {unreadCount > 0 && (
              <span className="w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setCurrentTab('send')}
            className={`px-4 py-1.5 rounded-md text-[12px] font-black transition-all flex items-center gap-2 ${currentTab === 'send' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Send size={13} /> Send Notification
          </button>
        </div>
      </div>

      {/* ── INBOX TAB ── */}
      {currentTab === 'inbox' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
            <span className="text-[12px] font-bold text-slate-500">{notifications.length} Total</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                disabled={markingAll}
                className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-black text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md border border-emerald-100 transition-colors"
              >
                {markingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} strokeWidth={3} />}
                Mark All Read
              </button>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="animate-spin text-indigo-400" size={28} />
              <p className="text-[13px] font-bold text-slate-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-300">
              <Bell size={40} />
              <p className="text-[13px] font-bold text-slate-400">No notifications yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {notifications.map((notif, idx) => {
                const isUnread = !notif.read_at;
                const data = notif.data || {};
                const type = notif.type || data.type || '';
                const meta = getNotifMeta(type);
                const Icon = meta.icon;
                return (
                  <div
                    key={notif.id || idx}
                    onClick={() => handleNotifClick(notif)}
                    className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-indigo-50/60 active:bg-indigo-100/60 ${isUnread ? 'bg-indigo-50/30 hover:bg-indigo-50/50' : 'hover:bg-gray-50/50'}`}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center shrink-0 mt-0.5`}>
                      <Icon size={18} className={meta.color} strokeWidth={2.5} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-black uppercase tracking-wider ${meta.color} bg-opacity-10 ${meta.bg} px-2 py-0.5 rounded border ${meta.border}`}>
                          {meta.label}
                        </span>
                        {isUnread && (
                          <span className="flex items-center gap-1 text-[9px] font-black text-red-500 uppercase tracking-wider animate-pulse">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full inline-block"></span>
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-[13px] font-bold text-slate-800 mt-1 leading-snug">
                        {data.title || data.message || 'New notification'}
                      </p>
                      {data.body && data.body !== data.message && (
                        <p className="text-[11px] font-semibold text-slate-500 mt-0.5 line-clamp-2">{data.body}</p>
                      )}
                      {(data.employee_name || data.user_name) && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <User size={11} className="text-slate-400" />
                          <span className="text-[11px] font-bold text-slate-500">{data.employee_name || data.user_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Time */}
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] font-bold text-slate-400">{timeAgo(notif.created_at)}</span>
                      {isUnread && <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── SEND TAB ── */}
      {currentTab === 'send' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 max-w-xl">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <Send size={15} className="text-emerald-600" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-[14px] font-black text-slate-800">Broadcast Notification</h2>
              <p className="text-[10px] font-semibold text-slate-400">Send real-time message to all active employees.</p>
            </div>
          </div>

          {sendStatus === 'success' && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-3 rounded-lg mb-5 animate-in slide-in-from-top-2">
              <CheckCircle2 size={18} strokeWidth={2.5} />
              <p className="text-[13px] font-bold">Notification broadcasted to all employees successfully!</p>
            </div>
          )}
          {sendStatus === 'error' && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg mb-5 animate-in slide-in-from-top-2">
              <AlertCircle size={18} strokeWidth={2.5} />
              <p className="text-[13px] font-bold">{sendError}</p>
            </div>
          )}

          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Notification Title *</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Office Holiday Announcement"
                required
                maxLength={100}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-[13px] font-semibold text-slate-800 transition-all"
              />
              <p className="text-[9px] font-semibold text-slate-400 text-right">{title.length}/100</p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Message Body *</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Enter notification details..."
                required
                maxLength={1000}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-[13px] font-semibold text-slate-800 transition-all resize-none"
              />
              <p className="text-[9px] font-semibold text-slate-400 text-right">{message.length}/1000</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={sending || !title.trim() || !message.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-[#10b981] hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-[12px] rounded-lg transition-colors shadow-sm"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />}
                {sending ? 'Sending...' : 'Broadcast to All Employees'}
              </button>
              <button
                type="button"
                onClick={() => { setTitle(''); setMessage(''); setSendStatus(null); }}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 font-bold text-[12px] rounded-lg hover:bg-slate-50 transition-colors"
              >
                <X size={13} /> Clear
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
              <AlertCircle size={12} className="text-amber-500 shrink-0" />
              <p className="text-[10px] font-semibold text-amber-700">This sends a push notification to all active employee mobile apps.</p>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
